import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../services/api';
import CreateProjectModal from '../components/CreateProjectModal';
import {
  FolderIcon,
  FolderPlusIcon,
  RectangleStackIcon,
  CheckCircleIcon,
  ClockIcon,
  BookOpenIcon,
  ChartBarIcon,
  VideoCameraIcon,
  PlayCircleIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import { FEATURES } from '../config/phases';

// 19.4's tile set is Projects / Videos / Runs completed / Approved findings
// (the fourth is the explicit swap of the main app's green "Average Score"
// badge for a green "Approved findings" count, plan ~line 1672). That leaves
// `in_progress_runs` - also named in 19.4's tile list - without its own
// tile; rather than drop it or invent a fifth tile (the plan says "four
// tiles" twice), it is surfaced as a caption on the Runs completed tile,
// matching the footer-strip recipe ProjectDashboardPage already uses for its
// own Videos/Findings tiles (6.3). Called out for the phase report as a
// DECISION, since the plan text does not resolve this itself.
const ACTIVITY_ICONS = {
  video_uploaded: VideoCameraIcon,
  run_started: PlayCircleIcon,
  run_completed: CheckCircleIcon,
  run_failed: ClockIcon,
  report_row_approved: ClipboardDocumentCheckIcon,
};

function getActivityIcon(type) {
  return ACTIVITY_ICONS[type] || ClockIcon;
}

// The most useful destination for each activity type, or null when the row cannot be
// linked. project_id/video_id are populated on every ActivityItem the backend can trace
// back to a project and video (dashboard.py); a row missing either renders as plain text
// rather than a link that goes nowhere (this file's BUILD rule 2).
//
// video_uploaded/run_started/run_failed point at the video overview, where the upload,
// live progress or the failure state actually shows up; run_completed/report_row_approved
// point straight at the review queue, since that is where a completed run or an approved
// report row is actually acted on next. The review queue link is additionally gated on
// FEATURES.projectReview -- the same flag VideoOverviewPage's own "Open review queue"
// link already checks -- so this row can never point at review before that phase lands.
function getActivityHref(activity) {
  const projectId = activity.project_id;
  const videoId = activity.video_id;
  if (!projectId || !videoId) return null;

  switch (activity.type) {
    case 'video_uploaded':
    case 'run_started':
    case 'run_failed':
      return `/projects/${projectId}/videos/${videoId}`;
    case 'run_completed':
    case 'report_row_approved':
      return FEATURES.projectReview
        ? `/projects/${projectId}/videos/${videoId}/review`
        : null;
    default:
      return null;
  }
}

function getStatusColor(status) {
  switch (status) {
    case 'completed':
      return 'text-green-600 bg-green-100';
    case 'processing':
      return 'text-primary-600 bg-primary-100';
    case 'failed':
      return 'text-red-600 bg-red-100';
    default:
      return 'text-gray-600 bg-[#F8F8FF]';
  }
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

  const { data: dashboardData, isLoading, error } = useQuery(
    'dashboard-metrics',
    () => dashboardAPI.metrics(),
    {
      select: (response) => response.data,
      retry: 3,
      refetchOnWindowFocus: false,
    }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8F8FF]">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F8FF]">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-sm text-red-600">
              Failed to load dashboard data. Please try again later.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const metrics = dashboardData?.metrics || {};
  const recentActivity = dashboardData?.recent_activity || [];

  return (
    <div className="min-h-screen bg-[#F8F8FF]">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Create projects to organize your video compliance walkthroughs.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FolderIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Projects</dt>
                    <dd className="text-lg font-medium text-gray-900">{metrics.total_projects ?? 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <RectangleStackIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Videos &amp; photos</dt>
                    <dd className="text-lg font-medium text-gray-900">{metrics.total_videos ?? 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Runs completed</dt>
                    <dd className="text-lg font-medium text-gray-900">{metrics.completed_runs ?? 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-[#F8F8FF] px-5 py-3">
              <span className="text-sm text-blue-600 font-medium">
                {metrics.in_progress_runs ?? 0} in progress
              </span>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-6 w-6 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Approved report rows</dt>
                    <dd className="text-lg font-medium text-gray-900">{metrics.approved_findings ?? 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-[#F8F8FF] px-5 py-3">
              <span className="text-sm text-gray-600 font-medium">
                of {metrics.total_findings ?? 0} report rows
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-4">
                {/* A named button, not a heading inside one (QA 2026-09-29: no accessible name). */}
                <button
                  type="button"
                  onClick={() => setShowCreateProjectModal(true)}
                  aria-label="Create project"
                  aria-describedby="quick-create-project-hint"
                  className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-[#F8F8FF] transition-colors duration-200 text-left"
                >
                  <div className="flex-shrink-0 bg-primary-500 p-2 rounded-md">
                    <FolderPlusIcon className="h-5 w-5 text-white" />
                  </div>
                  <div className="ml-4">
                    <span className="block text-sm font-medium text-gray-900">Create Project</span>
                    <span id="quick-create-project-hint" className="block text-sm text-gray-500">
                      Start a new video compliance project
                    </span>
                  </div>
                </button>

                {isAdmin && (
                  <Link
                    to="/standards"
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-[#F8F8FF] transition-colors duration-200"
                  >
                    <div className="flex-shrink-0 bg-purple-500 p-2 rounded-md">
                      <BookOpenIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="ml-4">
                      <h4 className="text-sm font-medium text-gray-900">Browse Standards</h4>
                      <p className="text-sm text-gray-500">Explore certification standards</p>
                    </div>
                  </Link>
                )}

                {isAdmin && FEATURES.adminMetrics && (
                  <Link
                    to="/admin/metrics"
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-[#F8F8FF] transition-colors duration-200"
                  >
                    <div className="flex-shrink-0 bg-blue-500 p-2 rounded-md">
                      <ChartBarIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="ml-4">
                      <h4 className="text-sm font-medium text-gray-900">Metrics &amp; logs</h4>
                      <p className="text-sm text-gray-500">Model usage, cost and run logs</p>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Recent Activity</h3>
              <div className="flow-root">
                {recentActivity.length > 0 ? (
                  <ul className="-mb-8">
                    {recentActivity.map((activity, activityIdx) => {
                      const Icon = getActivityIcon(activity.type);
                      const href = getActivityHref(activity);
                      const RowTag = href ? Link : 'div';
                      const rowProps = href
                        ? { to: href, className: 'relative flex space-x-3 -m-2 p-2 rounded-md hover:bg-[#F8F8FF] transition-colors duration-200' }
                        : { className: 'relative flex space-x-3' };
                      return (
                        <li key={activity.id}>
                          <div className="relative pb-8">
                            {activityIdx !== recentActivity.length - 1 ? (
                              <span
                                className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                                aria-hidden="true"
                              />
                            ) : null}
                            <RowTag {...rowProps}>
                              <div>
                                <span className="h-8 w-8 rounded-full bg-[#F8F8FF] flex items-center justify-center">
                                  <Icon className="h-4 w-4 text-gray-600" />
                                </span>
                              </div>
                              <div className="min-w-0 flex-1 pt-1.5 flex justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm text-gray-900 truncate" title={activity.title}>
                                    {activity.title}
                                  </p>
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                      activity.status
                                    )}`}
                                  >
                                    {activity.status}
                                  </span>
                                </div>
                                <div className="flex-shrink-0 text-right text-sm whitespace-nowrap text-gray-500">
                                  {formatRelativeTime(activity.time)}
                                </div>
                              </div>
                            </RowTag>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="text-center py-8">
                    <ClockIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-primary-50 border border-primary-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <BookOpenIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-primary-800">Getting Started</h3>
              <div className="mt-2 text-sm text-primary-700">
                <p>New to the Virtual Audit demo? Here's the video-compliance flow:</p>
                <ol className="mt-2 list-decimal list-inside space-y-1">
                  <li>Create a project</li>
                  <li>Upload a walkthrough video</li>
                  <li>Start processing</li>
                  <li>Review the findings</li>
                  <li>Print the report</li>
                </ol>
              </div>
              <div className="mt-4">
                {/* "Create Your First Project" only while there is none (QA 2026-09-29: it
                    showed beside a Projects count of 4). Same destination either way. */}
                <Link
                  to="/projects"
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 transition-colors duration-200"
                >
                  {metrics.total_projects > 0 ? 'Go to your projects' : 'Create Your First Project'}
                </Link>
              </div>
            </div>
          </div>
        </div>

        <CreateProjectModal
          isOpen={showCreateProjectModal}
          onClose={() => setShowCreateProjectModal(false)}
        />
      </div>
    </div>
  );
}
